import * as React from 'react'
import _ from 'lodash'

import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import MuiAccordion from '@mui/material/Accordion'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'

import { I18n, useTranslation } from 'components/hooks/use-translation'
import { useResourcesOverview } from 'components/hooks/state'
import useWindowSize from 'components/hooks/use-window-size'
import ResourceItem from 'components/atomic/organisms/syllabus/components/resource-item'
import IconButton from 'components/atomic/atoms/icon-button'
import Loader from 'components/atomic/atoms/loader'
import Button from 'components/atomic/atoms/button'
import Icon from 'components/atomic/atoms/icon'
import Text from 'components/atomic/atoms/text'
import Card from 'components/atomic/atoms/card'
import Chip from 'components/atomic/atoms/chip'

import { Module, Progress, ResourceProgress } from 'types/types'

const Accordion = styled(MuiAccordion)(({ theme }) => ({
    'padding': '1rem',
    'border': `1px solid ${theme.palette.divider}`,
    'borderRadius': '4px',
    '&:before': {
        display: 'none',
    },
    '&.MuiPaper-root.Mui-expanded': {
        border: `1px solid ${theme.palette.grey[500]}`,
    },
}))

const AccordionSummary = styled(MuiAccordionSummary)`
    padding: 0;
    & .MuiAccordionSummary-content {
        margin: 0;
        justify-content: space-between;
    }
`

const AccordionDetails = styled(MuiAccordionDetails)`
    padding: 0;
    padding-top: 1rem;
    gap: 1rem;
    display: flex;
    flex-direction: column;
`

interface ModuleItemProps {
    index: number
    rootId: string
    rootType: 'path' | 'course'
    module: Module
    moduleProgress?: Progress
    resourceIds: string[]
    resourceProgress?: ResourceProgress[]
}

const ModuleItem = (props: ModuleItemProps) => {
    const { rootId, rootType, module, moduleProgress, resourceProgress, resourceIds, index } = props

    const [showContent, setShowContent] = React.useState(true)
    const [expanded, setExpanded] = React.useState(false)

    const { palette, breakpoints } = useTheme()
    const { width } = useWindowSize()
    const { t }: I18n = useTranslation()

    const { data: resources } = useResourcesOverview({
        shouldFetch: showContent,
        resources: resourceIds,
    })

    const isMobile = React.useMemo(() => width <= breakpoints.values.sm, [width, breakpoints])

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded((prev) => !prev)}
            disableGutters
            elevation={0}
            square>
            <AccordionSummary
                expandIcon={
                    <IconButton
                        aria-label="expand section"
                        color={expanded ? 'primary' : 'inherit'}
                        icon="expandMore"
                    />
                }
                aria-controls={`panel-${index + 1}-content`}
                id={`panel-${index + 1}-header`}>
                <div>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Text variant="body1" fontWeight="bold">{`${index + 1}. ${module.title}`}</Text>
                        {moduleProgress?.completed && !isMobile && (
                            <Chip
                                color="primary"
                                size="small"
                                variant="filled"
                                label={`${t('path:status.COMPLETED')}`}
                                rounded={false}
                            />
                        )}

                        {moduleProgress?.started && !moduleProgress?.completed && !isMobile && (
                            <Chip
                                sx={{ background: palette.grey[800], marginRight: '1rem', minWidth: '5.5rem' }}
                                size="small"
                                variant="filled"
                                label={`${t('path:status.IN_PROGRESS')}`}
                                rounded={false}
                            />
                        )}
                    </Stack>
                    <Text variant="subtitle2" mt={1}>
                        {isMobile ? _.truncate(module.short_description) : module.short_description}
                    </Text>
                </div>

                {moduleProgress?.completed && isMobile && <Icon name="taskAliIcon" />}
            </AccordionSummary>

            <AccordionDetails>
                {moduleProgress?.completed && !showContent ? (
                    <Card status="primary">
                        <Stack spacing={1} alignItems="center" direction="column">
                            <Text color="primary" fontWeight={600}>
                                {t('path:completedModule')}
                            </Text>
                            <Button onClick={() => setShowContent(true)} size="small" endIcon={<AddIcon />}>
                                {t('path:seeContents')}
                            </Button>
                        </Stack>
                    </Card>
                ) : resources ? (
                    <Stack spacing={2} mt={3} ml={{ md: 2 }}>
                        {resources.map((resource, i) => (
                            <ResourceItem
                                key={resource._id}
                                isLast={resources.length - 1 === i}
                                rootId={rootId}
                                rootType={rootType}
                                resource={resource}
                                resourceProgress={resourceProgress?.find((p) => p.resource === resource._id)}
                            />
                        ))}
                    </Stack>
                ) : (
                    <Loader length={3} />
                )}
            </AccordionDetails>
        </Accordion>
    )
}

export default ModuleItem
